<template>
    <v-card class="background-2 round-card" flat>
        <v-card-text class="pa-3">
            <v-row
                v-for="(day, index) in days"
                :key="index"
                class="ma-0 mb-2 align-start"
            >
                <!-- Day label + switch -->
                <v-col cols="12" sm="3" class="pa-0 d-flex align-center" style="min-height: 48px;">
                    <v-switch
                        v-model="dayActive[index]"
                        :label="day"
                        color="primary"
                        dense
                        hide-details
                        class="mt-0"
                        @change="onDayToggle(index)"
                    ></v-switch>
                </v-col>

                <!-- Time slots -->
                <v-col cols="12" sm="9" class="pa-0 pl-sm-3">
                    <template v-if="dayActive[index]">
                        <div
                            v-for="(slot, slotIndex) in getSlotsForDay(index)"
                            :key="slotIndex"
                            class="d-flex align-center mb-1"
                        >
                            <v-text-field
                                :value="slot.start_time"
                                @input="updateSlot(index, slotIndex, 'start_time', $event)"
                                type="time"
                                label="Inizio"
                                outlined
                                dense
                                hide-details
                                style="max-width: 130px;"
                                class="mr-2"
                            ></v-text-field>
                            <span class="mr-2 grey--text">—</span>
                            <v-text-field
                                :value="slot.end_time"
                                @input="updateSlot(index, slotIndex, 'end_time', $event)"
                                type="time"
                                label="Fine"
                                outlined
                                dense
                                hide-details
                                style="max-width: 130px;"
                                class="mr-2"
                            ></v-text-field>
                            <v-btn
                                icon
                                small
                                color="error"
                                @click="removeSlot(index, slotIndex)"
                                :disabled="getSlotsForDay(index).length <= 1"
                            >
                                <v-icon small>mdi-close</v-icon>
                            </v-btn>
                        </div>

                        <v-btn
                            text
                            small
                            color="primary"
                            @click="addSlot(index)"
                            class="mt-1 pa-0"
                        >
                            <v-icon left small>mdi-plus</v-icon>
                            Aggiungi fascia
                        </v-btn>
                    </template>
                    <template v-else>
                        <span class="caption grey--text">Giorno non lavorativo</span>
                    </template>
                </v-col>

                <v-col cols="12" class="pa-0">
                    <v-divider v-if="index < days.length - 1" class="mt-2"></v-divider>
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>
</template>

<script>
// Props: value — array of { day_of_week, start_time, end_time }
// Emits: input (v-model pattern)
//
// Usage:
//   <working-hours-weekly-schedule v-model="form.schedules"></working-hours-weekly-schedule>

const DAYS = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica']

// Default Mon-Fri active with 09:00-13:00 and 14:00-18:00
const DEFAULT_ACTIVE = [true, true, true, true, true, false, false]

export default {
    name: 'WorkingHoursWeeklySchedule',

    props: {
        value: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            days: DAYS,
            // internal copy of schedules, keyed by day_of_week
            internalSlots: this.buildInternalSlots(this.value),
            dayActive: this.buildDayActive(this.value)
        }
    },

    watch: {
        value(newVal) {
            this.internalSlots = this.buildInternalSlots(newVal)
            this.dayActive = this.buildDayActive(newVal)
        }
    },

    methods: {
        buildInternalSlots(schedules) {
            const slots = {}
            for (let i = 0; i < 7; i++) slots[i] = []
            if (schedules && schedules.length) {
                schedules.forEach(s => {
                    if (!slots[s.day_of_week]) slots[s.day_of_week] = []
                    slots[s.day_of_week].push({ start_time: s.start_time, end_time: s.end_time })
                })
            } else {
                // Default Mon-Fri
                for (let i = 0; i < 5; i++) {
                    slots[i] = [
                        { start_time: '09:00', end_time: '13:00' },
                        { start_time: '14:00', end_time: '18:00' }
                    ]
                }
            }
            return slots
        },

        buildDayActive(schedules) {
            if (!schedules || schedules.length === 0) {
                return [...DEFAULT_ACTIVE]
            }
            const active = [false, false, false, false, false, false, false]
            schedules.forEach(s => {
                active[s.day_of_week] = true
            })
            return active
        },

        getSlotsForDay(dayIndex) {
            return this.internalSlots[dayIndex] || []
        },

        onDayToggle(dayIndex) {
            if (this.dayActive[dayIndex] && this.internalSlots[dayIndex].length === 0) {
                this.$set(this.internalSlots, dayIndex, [
                    { start_time: '09:00', end_time: '18:00' }
                ])
            }
            this.emitValue()
        },

        addSlot(dayIndex) {
            const slots = [...(this.internalSlots[dayIndex] || [])]
            slots.push({ start_time: '09:00', end_time: '18:00' })
            this.$set(this.internalSlots, dayIndex, slots)
            this.emitValue()
        },

        removeSlot(dayIndex, slotIndex) {
            const slots = [...(this.internalSlots[dayIndex] || [])]
            if (slots.length <= 1) return
            slots.splice(slotIndex, 1)
            this.$set(this.internalSlots, dayIndex, slots)
            this.emitValue()
        },

        updateSlot(dayIndex, slotIndex, field, value) {
            const slots = [...(this.internalSlots[dayIndex] || [])]
            slots[slotIndex] = { ...slots[slotIndex], [field]: value }
            this.$set(this.internalSlots, dayIndex, slots)
            this.emitValue()
        },

        emitValue() {
            const result = []
            for (let i = 0; i < 7; i++) {
                if (this.dayActive[i]) {
                    const slots = this.internalSlots[i] || []
                    slots.forEach(slot => {
                        result.push({
                            day_of_week: i,
                            start_time: slot.start_time,
                            end_time: slot.end_time
                        })
                    })
                }
            }
            this.$emit('input', result)
        }
    }
}
</script>
